<template>
  <Dialog
    :visible="visible"
    modal
    :header="$t('UserProjects.settings.closure.title')"
    :style="{ width: '560px', maxWidth: '95vw' }"
    :closable="!submitting"
    data-testid="project-closure-request-wizard"
    @update:visible="$emit('update:visible', $event)"
  >
    <p class="text-[13px] text-[var(--color-disabled)] mb-4">
      {{ $t('UserProjects.settings.closure.stepOf', { step: step + 1, label: stepLabels[step] }) }}
    </p>

    <div v-if="step === 0" class="flex flex-col gap-4">
      <p class="text-[14px] m-0">{{ $t('UserProjects.settings.closure.assessmentHint', { name: projectName }) }}</p>
      <label class="flex flex-col gap-1">
        <span class="text-[13px] font-medium">{{ $t('UserProjects.settings.closure.wentWell') }}</span>
        <Textarea v-model="wentWell" rows="3" class="w-full" />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[13px] font-medium">{{ $t('UserProjects.settings.closure.improveNext') }}</span>
        <Textarea v-model="improveNext" rows="3" class="w-full" />
      </label>
    </div>

    <div v-else-if="step === 1" class="flex flex-col gap-3">
      <Message severity="warn" :closable="false">
        {{ $t('UserProjects.settings.closure.pendingHint') }}
      </Message>
      <ul class="text-sm text-[var(--color-disabled)] m-0 pl-5 space-y-1">
        <li>{{ $t('UserProjects.settings.closure.effect1') }}</li>
        <li>{{ $t('UserProjects.settings.closure.effect2') }}</li>
        <li>{{ $t('UserProjects.settings.closure.effect3') }}</li>
      </ul>
    </div>

    <div v-else class="flex flex-col gap-3">
      <Checkbox
        v-model="confirmed"
        binary
        input-id="closure-request-confirm"
        data-testid="project-closure-request-confirm"
      />
      <label for="closure-request-confirm" class="text-sm">
        {{ $t('UserProjects.settings.closure.confirmLabel') }}
      </label>
    </div>

    <template #footer>
      <Button
        v-if="step > 0"
        :label="$t('UserProjects.settings.closure.back')"
        severity="secondary"
        text
        :disabled="submitting"
        @click="step -= 1"
      />
      <Button
        :label="$t('UserProjects.settings.cancel')"
        severity="secondary"
        text
        :disabled="submitting"
        @click="$emit('update:visible', false)"
      />
      <Button
        v-if="step < 2"
        :label="$t('UserProjects.settings.closure.next')"
        class="btn-primary-accent"
        unstyled
        @click="step += 1"
      />
      <Button
        v-else
        :label="$t('UserProjects.settings.closure.submit')"
        class="btn-primary-accent"
        unstyled
        :disabled="!confirmed"
        :loading="submitting"
        data-testid="project-closure-request-submit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import pmProjectSettingsService from '@/services/pmProjectSettingsService';

export default {
  name: 'ProjectClosureRequestWizard',
  components: { Dialog, Button, Textarea, Checkbox, Message },
  props: {
    visible: { type: Boolean, default: false },
    projectId: { type: String, required: true },
    projectName: { type: String, default: '' },
  },
  emits: ['update:visible', 'requested'],
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      step: 0,
      wentWell: '',
      improveNext: '',
      confirmed: false,
      submitting: false,
    };
  },
  computed: {
    stepLabels() {
      return [
        this.$t('UserProjects.settings.closure.steps.assessment'),
        this.$t('UserProjects.settings.closure.steps.summary'),
        this.$t('UserProjects.settings.closure.steps.confirm'),
      ];
    },
  },
  watch: {
    visible(next) {
      if (next) {
        this.step = 0;
        this.wentWell = '';
        this.improveNext = '';
        this.confirmed = false;
      }
    },
  },
  methods: {
    async submit() {
      if (!this.confirmed) return;
      this.submitting = true;
      try {
        await pmProjectSettingsService.requestClosure(this.projectId, {
          wentWell: this.wentWell,
          improveNext: this.improveNext,
        });
        this.toast.add({
          severity: 'success',
          summary: this.$t('UserProjects.settings.closure.toastRequested'),
          life: 4000,
        });
        this.$emit('requested');
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('UserProjects.settings.closure.error'),
          life: 4000,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
